// Angular
import { ActivatedRoute, Router } from '@angular/router';
import {
	Component,
	EventEmitter,
	Input,
	Output,
	OnChanges,
	SimpleChanges, ElementRef
} from '@angular/core';
import {
	animate,
	state,
	style,
	transition,
	trigger
} from '@angular/animations';
// Model
import { NavItems } from '../nav/nav.model';
import { LeftNavMenuStyleModel } from './model/left-nav-menu-style.model';

export interface LeftNewNavItem extends NavItems {
	icon?: string;
	customClass?: string;
	customIconClass?: string;
	collapsible?: boolean;
	badge?: string;
	badgeIcon?: string;
	scrollable?: boolean;
	index?: number;
	showItemCounter?: boolean;
	hideDivider?: boolean;
	items?: Array<LeftNewNavItem>;
	actions?: NavbarActions
}

export enum CustomActions {
	OPEN_HELPDESK = 'OPEN_HELPDESK',
	CLOSE_HELPDESK = 'CLOSE_HELPDESK'
}

export interface NavbarActions {
	/** Action identifier triggered when the item is clicked */
	click?: CustomActions;
}

@Component({
	selector: 'cv-left-nav-new',
	templateUrl: './left-nav-new.component.html',
	styleUrls: ['./left-nav-new.component.scss'],
	animations: [
		trigger('collapse', [
			state('isOpen', style({ width: '{{width}}px' }), { params: { width: 210 } }),
			state('isClosed', style({ width: '50px' })),
			state('default', style({ transform: 'rotate(0)' })),
			state('rotated', style({ transform: 'rotate(-180deg)' })),
			transition('isOpen <=> isClosed', animate('200ms')),
			transition('rotated => default', animate('300ms ease-out')),
			transition('default => rotated', animate('300ms ease-in'))
		]),
		trigger('collapse-up', [
			state('opened', style({ display: 'contents' })),
			state('closed', style({ display: 'none' })),
			state('default', style({ transform: 'rotate(0)' })),
			state('rotated', style({ transform: 'rotate(-180deg)' })),
			transition('opened <=> closed', animate('200ms')),
			transition('rotated => default', animate('300ms ease-out')),
			transition('default => rotated', animate('300ms ease-in'))
		])
	]
})
/* eslint-disable @angular-eslint/no-output-on-prefix */
export class LeftNavNewComponent implements OnChanges {
	@Output() onExpand: EventEmitter<any> = new EventEmitter<any>();
	@Output() onCollapse: EventEmitter<any> = new EventEmitter<any>();
	@Output() onRouteChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() onCustomAction: EventEmitter<any> = new EventEmitter<any>();
	@Input() items: Array<LeftNewNavItem>;
	@Input() width: number;
	@Input() scrollableInto = false;
	@Input() uiConfigStyle: LeftNavMenuStyleModel = new LeftNavMenuStyleModel();
	@Input() tooltipPlacement: 'top' | 'bottom' | 'right' | 'left' = 'right';
	@Input() useNewTooltip = false;
	toggleState: string;
	navItems: Array<any>;
	collapsedStates: Map<number, string>;

	public selectedMenuItem: LeftNewNavItem = null;

	// https://github.com/angular/angular/issues/21457
	public preventRoutePropagation = false;

	constructor(
		public route: ActivatedRoute,
		public router: Router,
		private elementRef: ElementRef
	) {
		this.isOpen = true;
		this.width = 210;
		this.toggleState = 'isOpen';
		this.collapsedStates = new Map<number, string>();
	}

	_isOpen: boolean;

	get isOpen(): boolean {
		return this._isOpen;
	}

	@Input()
	set isOpen(value: boolean) {
		this._isOpen = value;
		this.toggleState = this._isOpen ? 'isOpen' : 'isClosed';
	}

	/**
	 * On Toggle switch click, collapse or expand component.
	 * It emits the corresponding expand/collapse events as well.
	 */
	onToggle(): void {
		this.isOpen = !this.isOpen;
		if (this.isOpen) {
			this.onExpand.emit();
		} else {
			this.onCollapse.emit();
		}
	}

	/**
	 * Is the component collapsed ?
	 */
	isCollapsed(): boolean {
		return this.toggleState === 'isClosed';
	}

	/**
	 * Returns toggle icon state based on the component state.
	 */
	getToggleIconState(): string {
		return this.isOpen ? 'default' : 'rotated';
	}

	/**
	 * Event triggers every time the route changes and sends the item:LeftNewNavItem that got selected.
	 * @param item: LeftNewNavItem
	 */
	onRouteClick(item: LeftNewNavItem): void {
		if (item.actions) {
			this.onCustomAction.emit(item.actions.click)
		} else {
			if (!this.preventRoutePropagation) {
				this.onRouteChange.emit(item);
			}
			this.preventRoutePropagation = false;
		}
	}

	/**
	 * Returns toggle icon state based on the component state.
	 */
	getCollapsedIconState(index: number): string {
		return this.collapsedStates && this.collapsedStates.has(index) && this.collapsedStates.get(index) === 'closed' ? 'rotated' : 'default';
	}

	/**
	 * On Toggle switch click, collapse or expand component.
	 * It emits the corresponding expand/collapse events as well.
	 */
	onToggleCollapse(index: number, event: Event): void {
		this.preventRoutePropagation = true;
		if (this.collapsedStates.has(index)) {
			const newState = (this.collapsedStates.get(index) === 'opened' ? 'closed' : 'opened');
			this.collapsedStates.set(index, newState);
		} else {
			this.collapsedStates.set(index, 'closed');
		}
	}

	/**
	 * Keep the last select element with all components
	 * @param item
	 */
	onSelectedItemChange(item: LeftNewNavItem): void {
		this.selectedMenuItem = item;
	}

	ngOnChanges(changes: SimpleChanges): void {
		for (const propName in changes) {
			if (changes[propName]) {
				switch (propName) {
					case 'items': {
						this.navItems = Object.assign([], this.items) || [];
						break;
					}
					case 'uiConfigStyle': {
						this.compileStyle();
						break;
					}
				}
			}
		}
	}

	/**
	 * For the given element compile a new set of colors
	 * Some are possible to be changed by ngStyle, but others like :hover, :focus only exists as seudo-classes
	 * this ensure that those extra classes are properly compiled on the Component
	 */
	public compileStyle(): void {
		if (this.uiConfigStyle !== null) {
			const style = `
				.cv-left-nav .container .nav.add-bottom-border { border-bottom: solid 1px ${this.uiConfigStyle.item?.separatorColor} !important; }
				.cv-left-nav .nav-link:hover { background: ${this.uiConfigStyle.item?.active?.backgroundColor} !important; }
				.cv-left-nav .nav-link:hover .item-label { color: ${this.uiConfigStyle.item?.active?.textColor} !important; }
				.cv-left-nav .nav-link.active .nav-icon { color: ${this.uiConfigStyle.item?.active?.iconColor} !important; }
				.cv-left-nav:not(.is-collapsed) .nav-link.active:last-child { background: ${this.uiConfigStyle.item?.active?.backgroundColor} !important; }
			`;
			const styleElement = document.createElement('style');
			styleElement.appendChild(document.createTextNode(style));
			this.elementRef.nativeElement.appendChild(styleElement);
		}
	}

	/**
	 * Sets the menu item as active (search by id or route). All others are set to inactive.
	 * @param item: LeftNewNavItem
	 */
	public async setItemAsActive(itemId: any): Promise<void> {
		if (!itemId || itemId === '') {
			return;
		}

		const elements = [];
		await this.getItems(itemId, this.navItems, elements);

		if (elements.length > 0) {
			// Activate match elements
			setTimeout(() => {
				this.selectedMenuItem = elements[0];
				elements.forEach((item: any) => {
					item.active = true;
				});
				// Configurable
				if (this.scrollableInto) {
					setTimeout(() => {
						this.scrollIntoItemView();
					}, 400);
				}
			});
		}
	}

	/**
	 * Scroll into the view if the element is not on the view Port
	 * @private
	 */
	private scrollIntoItemView(): void {
		// After the menu is being selected, we just scroll to it
		const elements = document.body.querySelectorAll('cv-left-nav-new li.nav-item a.nav-link.active');
		let elementToScroll = null;
		// It means we have selected one child and not just the upper Level
		if (elements.length > 1) {
			elementToScroll = elements[elements.length - 1];
		} else if (elements.length === 1) {
			// Just one Level
			elementToScroll = elements[0];
		}
		// Element exist and is not visible
		if (elementToScroll !== null && !this.isItemOnViewPort(elementToScroll)) {
			// If Item not visible, then we scroll into it
			elementToScroll.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
		}
	}

	/**
	 * Determinate if element is available in the View Port
	 * @private
	 */
	private isItemOnViewPort(element: any): boolean {
		const headersHeight = 110;
		const bottomHeight = 95;
		// Get it's position in the viewport
		const bounding = element.getBoundingClientRect();
		return (
			bounding.top >= headersHeight &&
			bounding.bottom <= ((window.innerHeight || document.documentElement.clientHeight) - bottomHeight)
		);
	}

	/**
	 * Iterate recursively to get the Element that match the id
	 * @param itemId
	 * @private
	 */
	private async getItems(itemId: any, items: any, elements: any): Promise<boolean> {
		// Current Match Element
		let elementCycle = null;
		// Deactivate all elements
		items.forEach((e) => {
			e.active = false;
		});
		// Search for the element
		for (let i = 0; i < items.length; i++) {
			if (items[i].id === itemId) {
				elements.push(items[i]);
				elementCycle = items[i];
			}
			if (items[i].items) {
				const item = await this.getItems(itemId, items[i].items, elements);
				if (item !== null) {
					elements.push(items[i]);
					elementCycle = items[i];
				}
			}
		}
		return elementCycle;
	}

	// ---

	/**
	 * Pass when Angular detects the item is the current route selected
	 * so we can save latest element selected
	 * @param item
	 */
	onRouterLinkActive(item: LeftNewNavItem): boolean {
		if (!item.scrollable && this.router.url === item.route) {
			this.onSelectedItemChange(item);
			return true;
		}
		return false;
	}

	/**
	 * If enable events flag is on, then user can't route navigate.
	 */
	canNavigate(item: LeftNewNavItem): boolean {
		if (this.selectedMenuItem !== null && !this.selectedMenuItem.scrollable) {
			// It means we are on none scrollable element and we want to go to probably an scrollable item
			return true;
		}

		return item.scrollable ? false : true;
	}

	/**
	 * Return the correct icon class to apply based on item properties.
	 * @param item: LeftNewNavItem
	 */
	getIconClass(item: LeftNewNavItem): string {
		if (!item.label || item.label === '') {
			return 'fas fa-exclamation-circle not-defined-item-label';
		} else if (item.customIconClass) {
			return item.customIconClass;
		} else if (item.icon && item.icon !== 'none') {
			return `fa ${item.icon}`;
		} else if (item.icon && item.icon === 'none') {
			return 'none';
		} else {
			return 'default-child-icon fa fa-circle';
		}
	}
}
